import {
    forwardRef,
    useImperativeHandle,
    useState,
    useContext,
    useRef,
} from 'react'

import {
    useMutation,
} from '@tanstack/react-query'

import DisagreeDialog from './Components/DisagreeDialog'
import PaperProcessInfoDialog from './Components/PaperProcessInfoDialog'

import requestPaperFormMutation from './services/request-paper-form'
import confirmPaperProcessMutation from './services/confirm-paper-process'

import ApplicantDataFormContext from "../../../../Contexts/ApplicantDataFormContext"
import Overlay from '../../../../Components/Overlay'

const DisclosureForElectronicConsent = forwardRef(({
    data,
    company,
    refetchFormHandler,
    refToPrint,
    employmentLocation,
}, ref) => {

    const applicantDataForm = useContext(ApplicantDataFormContext)

    const requestKey = applicantDataForm.current["requestKey"]

    const [consent, setConsent] = useState(data?.consent ?? "")

    const [error, setError] = useState("")

    const [showDisagreeDialog, setShowDisagreeDialog] = useState(false)

    const [showPaperProcessInfoDialog, setShowPaperProcessInfoDialog] = useState(false)

    const {
        error: requestPaperFormMutationError,
        isPending: requestPaperFormMutationIsPending,
        mutate: requestPaperForm,
        data: requestPaperFormMutationData,
    } = useMutation({
        mutationFn: requestPaperFormMutation,
        onSuccess: async (d) => {
            setShowDisagreeDialog(false)
            setShowPaperProcessInfoDialog(true)
        }
    })

    const {
        error: confirmPaperProcessMutationError,
        isPending: confirmPaperProcessmMutationIsPending,
        mutate: confirmPaperProcess,
        data: confirmPaperProcessMutationData,
    } = useMutation({
        mutationFn: confirmPaperProcessMutation,
        onSuccess: async (d) => {
            setShowPaperProcessInfoDialog(false)
            refetchFormHandler?.()
        }
    })

    const requestPaperFormHandler = () => {
        requestPaperForm({
            key: requestKey,
        })
    }

    const continueToElectronicHandler = () => {
        setShowDisagreeDialog(false)
    }

    const revertToElectronicHandler = () => {
        setShowPaperProcessInfoDialog(false)
    }

    const confirmPaperProcessHandler = () => {
        confirmPaperProcess({
            key: requestKey,
        })
    }

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {

            const isValid = consent === "agree"

            if (!consent) {
                setError("You must indicate whether you agree or do not agree before continuing.")
            }

            if (consent === "disagree") {
                setShowDisagreeDialog(true)
            }

            return {
                isValid: isValid,
                value: {
                    consent,
                },
            }
        },
    }))

    return (
        <div ref={refToPrint} className=" flex items-center justify-center px-4 py-10">
            <div className="max-w-3xl w-full bg-white shadow-xl rounded-2xl p-8 space-y-6">
                <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-gray-800">
                        Electronic Document and Signature Agreement
                    </h1>
                    <p className="text-gray-700 text-base">
                        This page outlines your agreement to conduct the background check process electronically
                        with <span className="font-semibold">VerifiedID</span>, pursuant to the <em>Electronic Signatures
                            in Global and National Commerce Act (ESIGN Act)</em> and the <em>Uniform Electronic Transaction Act (UETA)</em>.
                    </p>
                </div>

                <div className="bg-gray-100 p-4 rounded-lg space-y-3 text-gray-700 text-sm">
                    <p>By proceeding, you agree to:</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li>Receive and sign all background check documents electronically. Your electronic signature will be legally binding.</li>
                        <li>Have the necessary technology (internet access, browser, PDF reader) to view these documents.</li>
                        <li>
                            Understand that you can request paper copies or withdraw your consent to electronic processing by
                            contacting <span className="font-semibold">VerifiedID</span> at {" "}
                            {
                                employmentLocation?.country !== "India" && (
                                    <>
                                        <a href="tel:+18887182597" className="text-blue-600 underline">
                                            (888) 718-2597
                                        </a>{" "}
                                        or{" "}
                                    </>
                                )
                            }
                            <a href="mailto:support@verifiedid.co" className="text-blue-600 underline">support@verifiedid.co</a>. Opting out may cause delays and fees.
                        </li>
                    </ul>
                </div>

                <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-800">Consent to Electronic Records</h2>
                    <p className="text-gray-700 text-sm">
                        Do you agree to conduct the background screening process electronically through VerifiedID?
                    </p>

                    <div className="space-y-8">
                        <label className="flex items-start space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="consent"
                                value="agree"
                                checked={consent === "agree"}
                                onChange={() => setConsent("agree")}
                                className="mt-1"
                            />
                            <span className="text-gray-700 text-sm">
                                I AGREE
                            </span>
                        </label>

                        <label className="flex items-start space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="consent"
                                value="disagree"
                                checked={consent === "disagree"}
                                onChange={() => setConsent("disagree")}
                                className="mt-1"
                            />
                            <span className="text-gray-700 text-sm">
                                I do <strong>NOT</strong> agree. I prefer to print out and sign paper versions of the documents and return them by mail or
                                in person to <span className="font-semibold">{company?.name}</span>. I understand this may delay the selection process.
                            </span>
                        </label>
                    </div>
                </div>

                {error && (
                    <p className="mt-2 text-sm text-red-600">{error}</p>
                )}

            </div>

            {showDisagreeDialog && (
                <DisagreeDialog
                    requestPaperFormHandler={requestPaperFormHandler}
                    continueToElectronicHandler={continueToElectronicHandler}
                    employmentLocation={employmentLocation}
                />
            )}

            {showPaperProcessInfoDialog && (
                <PaperProcessInfoDialog
                    revertToElectronicHandler={revertToElectronicHandler}
                    confirmPaperProcessHandler={confirmPaperProcessHandler}
                    employmentLocation={employmentLocation}
                />
            )}

            {/* Overlay */}
            {
                (requestPaperFormMutationIsPending || confirmPaperProcessmMutationIsPending) &&
                <Overlay />
            }

        </div>
    )

})

export default DisclosureForElectronicConsent
